// skinRejuvenationData.ts

interface MetaData {
    title: string;
    description: string;
    robots?: string;
    canonical?: string;
    og?: {
        locale?: string;
        type?: string;
        title?: string;
        description?: string;
        url?: string;
        site_name?: string;
        updated_time?: string;
        image?: string;
        image_secure_url?: string;
        image_width?: number;
        image_height?: number;
        image_alt?: string;
        image_type?: string;
        video?: string;
        video_duration?: number;
        video_upload_date?: string;
        video_allow_embed?: boolean;
    };
    twitter?: {
        card?: string;
        title?: string;
        description?: string;
        image?: string;
    };
}

interface Step {
    title: string;
    desc: string;
    icon: string;
}

interface ProcedureSteps {
    heading: string;
    para: string;
    steps: Step[];
}

interface ListItem {
    title: string;
    desc: string;
    icon: string;
}

interface ListSection {
    heading?: string;
    subheading?: string;
    para?: string;
    items: ListItem[];
}

interface FAQItem {
    question: string;
    answer: string;
}

interface FAQSection {
    heading?: string;
    subheading?: string;
    items: FAQItem[];
}

interface HeroSection {
    title: string;
    subtitle: string;
    description: string;
}

interface HowItWorks {
    title: string;
    description: string;
}

interface TreatmentArea {
    ar: string;
    title: string;
    image: string;
}

interface LanguageContent {
    url?: string; // Added url property
    meta: MetaData;
    breadcrumbs: string[];
    hero: HeroSection;
    howItWorks: HowItWorks;
    procedureSteps: ProcedureSteps;
    benefits: ListSection;
    eligibility: ListSection;
    preparationTips: ListSection;
    results: ListSection;
    aftercareTips: ListSection;
    faqData: FAQSection;
    treatmentAreas1: TreatmentArea[];
    treatmentAreas2: TreatmentArea[];
}

interface SharedData {
    images: {
        main: string;
        beforeAfter: string[] | any;
        introductionImage?: string;
    };
    product: {
        id: number;
        title: string;
        price: number;
        description: string;
    };
}

export const skinRejuvenation: {
    en: LanguageContent;
    ar: LanguageContent;
    shared: SharedData;
} =
{
    "en": {
        "url": "",
        "meta": {
            "title": "Best Skin Rejuvenation Treatment in Dubai | Youthful Glowing Skin",
            "description": "Professional skin rejuvenation treatments in Dubai to reduce wrinkles, fine lines and restore youthful glow. Get smoother, firmer skin with our advanced procedures. Book your consultation today.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/services/skin-rejuvenation/",
            "og": {
                "locale": "en_US",
                "type": "article",
                "title": "Best Skin Rejuvenation Treatment in Dubai | Youthful Glowing Skin",
                "description": "Professional skin rejuvenation treatments in Dubai to reduce wrinkles, fine lines and restore youthful glow. Get smoother, firmer skin with our advanced procedures. Book your consultation today.",
                "url": "https://elitebodyhome.com/services/skin-rejuvenation/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "Skin Rejuvenation Treatment Dubai",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "Best Skin Rejuvenation Treatment in Dubai | Youthful Glowing Skin",
                "description": "Professional skin rejuvenation treatments in Dubai to reduce wrinkles, fine lines and restore youthful glow. Get smoother, firmer skin with our advanced procedures. Book your consultation today.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg"
            }
        },
        "breadcrumbs": ["Home", "Services", "Skin Rejuvenation"],
        "hero": {
            "title": "Skin Rejuvenation",
            "subtitle": "",
            "description": "Skin rejuvenation is a cosmetic procedure that helps people with the appearance of the skin that makes them look older, tired or less attractive. Through the years, aging, sun exposure, and environmental pollutants may cause wrinkles, fine lines, and an uneven skin texture. Skin rejuvenation procedures aim to correct these problems, and provide individuals with a smoother, firmer, and more youthful look."
        },
        "howItWorks": {
            "title": "Skin Rejuvenation Procedure",
            "description": "The skin revitalizing treatment is customized according to the individual's skin texture and requirement. These techniques work by triggering the skin's natural healing responses – which boosts collagen and helps to decrease the visibility of imperfections."
        },
        "procedureSteps": {
            "heading": "Our Skin Rejuvenation Treatments",
            "para": "",
            "steps": [
                {
                    "title": "Thread Lift",
                    "desc": "Non-surgical facelift that lifts and tightens sagging skin for immediate rejuvenation.",
                    "icon": "fa-solid fa-thread"
                },
                {
                    "title": "PRX-T33 Peel",
                    "desc": "Advanced chemical peel that stimulates collagen without downtime for radiant skin.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "Laser Rejuvenation",
                    "desc": "Resurfaces skin by removing damaged outer layers and stimulating new cell growth.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "Microneedling",
                    "desc": "Creates micro-channels to trigger collagen production for smoother texture.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "Pore Reduction",
                    "desc": "Minimizes enlarged pores for refined skin texture and clarity.",
                    "icon": "fa-solid fa-dot-circle"
                },
                {
                    "title": "Derma Pen",
                    "desc": "Precision microneedling device for targeted skin renewal and scar reduction.",
                    "icon": "fa-solid fa-pen"
                },
                {
                    "title": "Profhilo Skin Booster",
                    "desc": "Ultra-hydrating treatment that improves skin elasticity and firmness.",
                    "icon": "fa-solid fa-droplet"
                }
            ]
        },
        "benefits": {
            "heading": "Benefits of Skin Rejuvenation",
            "subheading": "",
            "para": "Receiving professional skin rejuvenation can do a lot to the skin in terms of undoing damage and improving the look of the skin. The treatments are targeted to help the surface layers of skin (such as dry, parched or sun-damaged skin) and the deep layers of skin, causing long-term improvements.",
            "items": [
                {
                    "title": "Fine Lines and Wrinkles improvement",
                    "desc": "Procedures stimulate collagen generation, blurring the lines of wrinkles.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "Improvement of Skin Texture",
                    "desc": "Uneven skin texture and enlarged pores are smoothed over, resulting in a more even and refined complexion.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "Enhanced Skin Tone",
                    "desc": "If your skin's dull or discolored, it can help brighten, since it evens pigmentation.",
                    "icon": "fa-solid fa-palette"
                },
                {
                    "title": "Reduction of Acne Scars",
                    "desc": "To manage the appearance of acne scars specifically, treatments like microneedling can help.",
                    "icon": "fa-solid fa-scroll"
                }
            ]
        },
        "eligibility": {
            "heading": "Eligibility",
            "subheading": "",
            "para": "What is best for you in skin rejuvenation can be determined by your skin type, concerns, and goals. These therapies are appropriate for many people, however not everyone may be a candidate for them.",
            "items": [
                {
                    "title": "Aging Individuals",
                    "desc": "If you start to notice fine lines, wrinkles and looseness to the skin, rejuvenation may be a good option.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "People with Sun Damage",
                    "desc": "Higher UV exposure leads to pigmentation problems and uneven texture – and both can be remedied.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Individuals with Acne Scars",
                    "desc": "Rejuvenation procedures, such as microneedling and chemical peels, can decrease the visibility of scars.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "Those with Dull Skin",
                    "desc": "Rejuvenation can refresh a dull complexion to a more vibrant, youthful look.",
                    "icon": "fa-solid fa-cloud-sun"
                }
            ]
        },
        "preparationTips": {
            "heading": "Preparation",
            "subheading": "",
            "para": "Preparation is essential to ensure the most out of skin rejuvenation procedures. By following the pre-treatment instructions of your clinician you can minimize risks and improve the results.",
            "items": [
                {
                    "title": "Avoid Sun Exposure",
                    "desc": "Avoid direct sun exposure and use sunscreen daily in the weeks prior to your surgery.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "Discontinue Retinoids",
                    "desc": "Halt all retinoid-based products and other aggressive skincare treatments at least a week prior to the treatment.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "Hydrate",
                    "desc": "Ensure to drink lots of water and follow a good skin care regimen to keep your skin hydrated.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "Specialist Referral",
                    "desc": "A thorough consultation will then guarantee a treatment that is customised to your personal requirements and skin type.",
                    "icon": "fa-solid fa-user-doctor"
                }
            ]
        },
        "results": {
            "heading": "Results",
            "subheading": "",
            "para": "The effects of skin rejuvenation procedures may be life-changing, leading to a marked improvement in skin texture, tone, and appearance. Outcomes may differ due to the techniques and skin conditions; however, people often enjoy refreshed, radiant skin.",
            "items": [
                {
                    "title": "Immediate Glow",
                    "desc": "With lots of treatments you can get an instant freshening, brightening effect.",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "Progressive Improvement",
                    "desc": "The production of collagen does not stop after the treatment, resulting in continual enhancement of the skin in the weeks or even months that follow the procedure.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "Long-lasting Results",
                    "desc": "The benefits of skin rejuvenation can be long-lasting with proper skin care.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "Frequently Asked Questions",
            "subheading": "",
            "items": [
                {
                    "question": "How long does a skin rejuvenation treatment take?",
                    "answer": "The duration depends on the type of treatment, but most procedures take between 30 minutes to an hour."
                },
                {
                    "question": "Are there any side effects?",
                    "answer": "Common side effects include mild redness, swelling, and sensitivity, which typically subside within a few days."
                },
                {
                    "question": "How many sessions are required for optimal results?",
                    "answer": "While some individuals see results after one session, others may need a series of treatments depending on the condition of their skin."
                },
                {
                    "question": "Can I return to my normal routine after the procedure?",
                    "answer": "Yes, most skin rejuvenation treatments require minimal downtime, allowing you to return to daily activities shortly after the procedure."
                },
                {
                    "question": "Is the treatment painful?",
                    "answer": "Most treatments involve minimal discomfort. Some clients may feel slight tingling or warmth, but it's generally well-tolerated."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    },
    "shared": {
        "images": {
            "main": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Skin-Rejuvenation-in-Dubai-2-scaled.jpg",
            "beforeAfter": [
                { src: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Skin-Rejuvenation-in-Elite-Body-Home-1.jpg", alt: "skin rejuventation" },
                { src: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Skin-Rejuvenation-in-Elite-Body-Home-2.jpg", alt: "skin rejuventation" },
                { src: "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/11/Skin-Rejuvenation-in-Elite-Body-Home-3.jpg", alt: "skin rejuventation" }
            ],
            "introductionImage": "https://example.com/intro-image.jpg"
        },
        "product": {
            "id": 315,
            "title": "Skin Rejuvenation Package",
            "price": 899,
            "description": "Comprehensive skin rejuvenation package including consultation, treatment sessions, and follow-up care. Our expert team offers advanced skin renewal solutions tailored to your needs."
        }
    },
    "ar": {
        "url": "",
        "meta": {
            "title": "أفضل علاج لتجديد البشرة في دبي | بشرة شبابية مشرقة",
            "description": "علاجات احترافية لتجديد البشرة في دبي لتقليل التجاعيد والخطوط الدقيقة واستعادة الإشراقة الشبابية. احصل على بشرة أكثر نعومة وتماسكًا مع إجراءاتنا المتطورة. احجز استشارتك اليوم.",
            "robots": "follow, index, max-snippet:-1, max-video-preview:-1, max-image-preview:large",
            "canonical": "https://elitebodyhome.com/ar/services/skin-rejuvenation/",
            "og": {
                "locale": "ar_AR",
                "type": "article",
                "title": "أفضل علاج لتجديد البشرة في دبي | بشرة شبابية مشرقة",
                "description": "علاجات احترافية لتجديد البشرة في دبي لتقليل التجاعيد والخطوط الدقيقة واستعادة الإشراقة الشبابية. احصل على بشرة أكثر نعومة وتماسكًا مع إجراءاتنا المتطورة. احجز استشارتك اليوم.",
                "url": "https://elitebodyhome.com/ar/services/skin-rejuvenation/",
                "site_name": "elitebodyhome.com",
                "updated_time": "2025-01-22T13:54:57+00:00",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_secure_url": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg",
                "image_width": 1254,
                "image_height": 700,
                "image_alt": "علاج تجديد البشرة دبي",
                "image_type": "image/jpeg"
            },
            "twitter": {
                "card": "summary_large_image",
                "title": "أفضل علاج لتجديد البشرة في دبي | بشرة شبابية مشرقة",
                "description": "علاجات احترافية لتجديد البشرة في دبي لتقليل التجاعيد والخطوط الدقيقة واستعادة الإشراقة الشبابية. احصل على بشرة أكثر نعومة وتماسكًا مع إجراءاتنا المتطورة. احجز استشارتك اليوم.",
                "image": "https://dev-elite.s3.me-central-1.amazonaws.com/wp-content/uploads/2024/10/Elite-Body-Home-Services-for-Astatic-Clinic-in-Dubai-38.jpg"
            }
        },
        "breadcrumbs": ["الرئيسية", "الخدمات", "تجديد البشرة"],
        "hero": {
            "title": "تجديد البشرة",
            "subtitle": "",
            "description": "يُعد علاج تجديد البشرة هو إجراء تجميلي يساعد الأشخاص الذين يعانون من بشرة تجعلهم يبدون أكبر سنًا، أو مرهقين، أو أقل جاذبية بسبب الشيخوخة والتعرض لأشعة الشمس والملوثات البيئية التي تؤدي إلى ظهور التجاعيد، والخطوط الدقيقة، وملمس بشرة غير متجانس مع مرور الوقت؛ لذلك تهدف هذه الإجراءات إلى تصحيح هذه المشكلات، وتوفير مظهر أكثر نعومة، وشبابًا."
        },
        "howItWorks": {
            "title": "إجراء تجديد البشرة",
            "description": "يتم تخصيص علاج تجديد البشرة حسب نوع البشرة واحتياجات كل فرد. تعتمد هذه التقنيات على تحفيز استجابة الجلد الطبيعية للشفاء، مما يعزز إنتاج الكولاجين ويساعد على تقليل مظهر العيوب."
        },
        "procedureSteps": {
            "heading": "تقنيات تجديد البشرة المتوفرة لدينا",
            "para": "",
            "steps": [
                {
                    "title": "شد الوجه بالخيوط",
                    "desc": "شد غير جراحي يرفع البشرة المترهلة ويشدها للحصول على مظهر شبابي فوري.",
                    "icon": "fa-solid fa-thread"
                },
                {
                    "title": "تقشير PRX-T33",
                    "desc": "تقشير كيميائي متطور يحفز الكولاجين دون الحاجة لفترة تعافي لبشرة مشرقة.",
                    "icon": "fa-solid fa-flask"
                },
                {
                    "title": "تجديد البشرة بالليزر",
                    "desc": "تجديد سطح البشرة بإزالة الطبقات التالفة وتحفيز نمو خلايا جديدة.",
                    "icon": "fa-solid fa-bolt"
                },
                {
                    "title": "الميكرونيدلينغ",
                    "desc": "إنشاء قنوات مجهرية لتحفيز إنتاج الكولاجين للحصول على ملمس أكثر نعومة.",
                    "icon": "fa-solid fa-syringe"
                },
                {
                    "title": "تصغير المسام الواسعة",
                    "desc": "تصغير المسام الكبيرة لتحسين ملمس البشرة ووضوحها.",
                    "icon": "fa-solid fa-dot-circle"
                },
                {
                    "title": "الديرما بن",
                    "desc": "جهاز دقيق للميكرونيدلينغ لتجديد البشرة المستهدف وتقليل الندوب.",
                    "icon": "fa-solid fa-pen"
                },
                {
                    "title": "إبرة النضارة بروفايلو",
                    "desc": "علاج مرطب للغاية يحسن مرونة البشرة وتماسكها.",
                    "icon": "fa-solid fa-droplet"
                }
            ]
        },
        "benefits": {
            "heading": "فوائد تجديد البشرة",
            "subheading": "",
            "para": "يقدم تجديد البشرة الاحترافي العديد من الفوائد من حيث إصلاح التلف وتحسين مظهر البشرة بشكل عام. تركز تقنيات العلاج على الطبقات السطحية للبشرة مثل البشرة الجافة أو التالفة من الشمس وكذلك الطبقات العميقة، مما يؤدي إلى تحسن يدوم طويلًا.",
            "items": [
                {
                    "title": "تحسين الخطوط الدقيقة والتجاعيد",
                    "desc": "تعمل الإجراءات على تحفيز إنتاج الكولاجين، مما يساعد على تقليل مظهر التجاعيد والخطوط الدقيقة.",
                    "icon": "fa-solid fa-face-smile"
                },
                {
                    "title": "تحسين ملمس البشرة",
                    "desc": "يساعد العلاج على تنعيم البشرة غير المتجانسة وتقليل حجم المسام، مما يمنح مظهرًا أكثر صفاءً وتناسقًا.",
                    "icon": "fa-solid fa-spa"
                },
                {
                    "title": "تفتيح لون البشرة",
                    "desc": "في حال وجود بهتان أو تفاوت في لون البشرة، تعمل هذه الإجراءات على توحيد اللون واستعادة الإشراقة الطبيعية.",
                    "icon": "fa-solid fa-palette"
                },
                {
                    "title": "التقليل من ندبات حب الشباب",
                    "desc": "تساهم بعض تقنيات العلاج مثل الميكرونيدلينغ في تقليل ندبات حب الشباب وتحسين مظهرها بشكل ملحوظ.",
                    "icon": "fa-solid fa-scroll"
                }
            ]
        },
        "eligibility": {
            "heading": "الأهلية",
            "subheading": "",
            "para": "تختلف ملاءمة إجراءات تجديد البشرة حسب نوع بشرتك، واحتياجاتك، والأهداف التي تسعى لتحقيقها. ورغم أن هذه الإجراءات مناسبة لشريحة واسعة من الأشخاص، إلا أن هناك بعض العوامل التي قد تؤثر في أهليتك للعلاج.",
            "items": [
                {
                    "title": "الأشخاص الذين تظهر عليهم علامات التقدم في العمر",
                    "desc": "إذا بدأت تلاحظ خطوطًا دقيقة، أو تجاعيد، أو ترهلًا خفيفًا في الجلد، فقد يكون تجديد البشرة خيارًا مناسبًا لك.",
                    "icon": "fa-solid fa-hourglass-half"
                },
                {
                    "title": "الأشخاص الذين يعانون من أضرار الشمس",
                    "desc": "يؤدي التعرض المفرط للأشعة فوق البنفسجية إلى تصبغات جلدية وعدم تجانس في ملمس البشرة، ويمكن لهذا العلاج أن يساعد في تحسين كليهما.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "الأشخاص الذين يعانون من ندبات حب الشباب",
                    "desc": "تُعد بعض أنواع العلاج مثل الميكرونيدلينغ والتقشير الكيميائي فعالة في تقليل مظهر الندبات وتحسين ملمس البشرة بشكل عام.",
                    "icon": "fa-solid fa-bacteria"
                },
                {
                    "title": "الأشخاص الذين يعانون من بشرة باهتة",
                    "desc": "إذا كانت بشرتك تفتقر إلى الإشراق، يمكن لعلاج التجديد أن يعيد لها الحيوية والنضارة والمظهر الشاب.",
                    "icon": "fa-solid fa-cloud-sun"
                }
            ]
        },
        "preparationTips": {
            "heading": "الاستعداد",
            "subheading": "",
            "para": "يُعد التحضير الجيد ضروري لتحقيق أفضل النتائج من إجراءات تجديد البشرة، كما أنه يقلل من احتمالية حدوث أي آثار جانبية. يمكنك ضمان تجربة آمنة وفعالة من خلال اتباع التعليمات التي يقدمها الطبيب.",
            "items": [
                {
                    "title": "تجنب التعرض لأشعة الشمس",
                    "desc": "تجنب التعرض المباشر لأشعة الشمس واستخدم واقي شمس يوميًا لعدة أسابيع قبل موعد الجلسة.",
                    "icon": "fa-solid fa-sun"
                },
                {
                    "title": "إيقاف استخدام الريتينويدات",
                    "desc": "توقف عن استخدام المنتجات التي تحتوي على الريتينويد أو إجراءات العناية بالبشرة القاسية، قبل أسبوع على الأقل من الجلسة.",
                    "icon": "fa-solid fa-ban"
                },
                {
                    "title": "الحفاظ على ترطيب جسمك",
                    "desc": "اشرب كميات كافية من الماء وحافظ على روتين ترطيب منتظم للحفاظ على رطوبة البشرة.",
                    "icon": "fa-solid fa-glass-water"
                },
                {
                    "title": "استشارة طبيب الجلدية",
                    "desc": "يجب حجز استشارة أولية لتقييم حالة بشرتك بدقة، واختيار نوع العلاج الأنسب بناءً على احتياجاتك ونوع بشرتك.",
                    "icon": "fa-solid fa-user-doctor"
                }
            ]
        },
        "results": {
            "heading": "النتائج",
            "subheading": "",
            "para": "يمكن أن تكون نتائج إجراءات تجديد البشرة مبهرة، حيث تساهم في تحسين ملمس البشرة، وتوحيد لونها، وتعزيز مظهرها العام. تختلف النتائج من شخص لآخر بناءً على التقنية المستخدمة وحالة البشرة، لكن يلاحظ معظم الأشخاص تغيرًا إيجابيًا واضحًا.",
            "items": [
                {
                    "title": "إشراقة فورية",
                    "desc": "تساهم بعض تقنيات العلاج في توفير إشراقة فورية ومظهراً منعشاً للبشرة بعد الجلسة مباشرةً.",
                    "icon": "fa-solid fa-sparkles"
                },
                {
                    "title": "تحسن تدريجي",
                    "desc": "يستمر تحفيز إنتاج الكولاجين حتى بعد انتهاء الجلسة، مما يؤدي إلى تحسن مستمر في مرونة ونضارة البشرة على مدى أسابيع أو حتى أشهر.",
                    "icon": "fa-solid fa-chart-line"
                },
                {
                    "title": "نتائج طويلة الأمد",
                    "desc": "يمكن أن تدوم النتائج لفترة طويلة، مما يمنحك بشرة أكثر شباباً وحيوية مع الالتزام بروتين عناية بالبشرة مناسب بعد العلاج.",
                    "icon": "fa-solid fa-calendar-check"
                }
            ]
        },
        "aftercareTips": {
            "heading": "",
            "subheading": "",
            "para": "",
            "items": []
        },
        "faqData": {
            "heading": "الأسئلة المكررة",
            "subheading": "",
            "items": [
                {
                    "question": "كم تستغرق جلسة تجديد البشرة؟",
                    "answer": "يعتمد الوقت على نوع العلاج، لكن تستغرق أغلب الجلسات ما بين 30 دقيقة إلى ساعة واحدة."
                },
                {
                    "question": "هل يسبب العلاج أي آثار جانبية؟",
                    "answer": "تشمل الآثار الجانبية الشائعة احمرارًا خفيفًا، أو تورمًا بسيطًا، أو حساسية في البشرة، وتختفي عادة خلال بضعة أيام."
                },
                {
                    "question": "كم عدد الجلسات المطلوبة للحصول على نتائج مثالية؟",
                    "answer": "يلاحظ بعض الأشخاص تحسنًا بعد جلسة واحدة، بينما قد يحتاج البعض الآخر إلى سلسلة من الجلسات حسب حالة البشرة ومدى تضررها."
                },
                {
                    "question": "هل يمكنني العودة إلى روتيني اليومي بعد الجلسة؟",
                    "answer": "نعم، لا تتطلب معظم جلسات تجديد البشرة فترة تعافي طويلة، ويمكنك العودة لممارسة أنشطتك اليومية بعد وقت قصير من الإجراء."
                },
                {
                    "question": "هل الجلسة مؤلمة؟",
                    "answer": "لا يسبب العلاج سوى قدر بسيط من الانزعاج. قد يشعر البعض بوخز خفيف أو دفء أثناء الجلسة، لكنها عادة ما تكون محتملة جدًا."
                }
            ]
        },
        "treatmentAreas1": [],
        "treatmentAreas2": []
    }
}
